import { useTranslation } from 'react-i18next';
import { WALLET_COLORS as C } from '@/constants';
import { formatWalletBalance } from '@/utils/wallet-display';
import type { WalletDailyPerf } from '@/utils/wallet-daily-perf';

function fmtUsd(raw: string): string {
  const n = formatWalletBalance(raw);
  return n === '0' ? '0$' : `${n}$`;
}

function LegBlock({
  t,
  title,
  register,
  reinvest,
  highlight,
}: {
  t: (key: string, fallback: string) => string;
  title: string;
  register: string;
  reinvest: string;
  highlight?: boolean;
}) {
  return (
    <div
      className="rounded-lg border p-3"
      style={{
        borderColor: highlight ? `${C.gold}88` : C.otherAsset,
        background: C.bgAsset,
        boxShadow: highlight ? `0 0 12px ${C.gold}22` : C.shadowAsset,
      }}
    >
      <div className="mb-2 text-xs font-semibold" style={{ color: highlight ? C.gold : C.text }}>
        {title}
      </div>
      <div className="grid grid-cols-2 gap-2 text-center">
        <div>
          <div className="text-sm font-semibold font-mono" style={{ color: C.text }}>{fmtUsd(register)}</div>
          <div className="mt-0.5 text-[9px]" style={{ color: C.muted }}>
            {t('wallet.perfDetail.register', '新注册业绩')}
          </div>
        </div>
        <div>
          <div className="text-sm font-semibold font-mono" style={{ color: C.text }}>{fmtUsd(reinvest)}</div>
          <div className="mt-0.5 text-[9px]" style={{ color: C.muted }}>
            {t('wallet.perfDetail.reinvest', '复投业绩')}
          </div>
        </div>
      </div>
    </div>
  );
}

export function DailyPerfModal({
  open,
  period,
  data,
  onClose,
}: {
  open: boolean;
  period: 'today' | 'yesterday' | null;
  data?: WalletDailyPerf;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  if (!open || !period || !data) return null;

  const slice = period === 'today' ? data.today : data.yesterday;
  const title =
    period === 'today'
      ? t('wallet.balances.today_perf', '今日业绩')
      : t('wallet.balances.yesterday_perf', '昨日业绩');
  const bigLeg =
    data.big_market_leg === 'R'
      ? t('wallet.perfDetail.bigLegR', '大区：右区')
      : t('wallet.perfDetail.bigLegL', '大区：左区');

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/70 p-4" onClick={onClose}>
      <div
        className="w-full max-w-[400px] rounded-t-2xl border p-4 pb-6"
        style={{ borderColor: C.otherAsset, background: '#07100A' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between gap-2">
          <div>
            <div className="text-base font-semibold" style={{ color: C.gold }}>{title}</div>
            <div className="mt-1 text-xs" style={{ color: C.muted }}>{bigLeg}</div>
          </div>
          <button type="button" onClick={onClose} className="text-sm font-semibold" style={{ color: C.muted }}>
            {t('common.close', '关闭')}
          </button>
        </div>

        <div className="mb-3 grid grid-cols-3 gap-2 text-center">
          <div className="rounded-lg border px-2 py-2" style={{ borderColor: C.otherAsset, background: C.bgAsset }}>
            <div className="text-sm font-semibold font-mono" style={{ color: C.gold }}>{fmtUsd(slice.total)}</div>
            <div className="mt-0.5 text-[9px]" style={{ color: C.muted }}>{t('wallet.perfDetail.total', '合计')}</div>
          </div>
          <div className="rounded-lg border px-2 py-2" style={{ borderColor: C.otherAsset, background: C.bgAsset }}>
            <div className="text-sm font-semibold font-mono" style={{ color: C.text }}>{fmtUsd(slice.register)}</div>
            <div className="mt-0.5 text-[9px]" style={{ color: C.muted }}>{t('wallet.perfDetail.register', '新注册')}</div>
          </div>
          <div className="rounded-lg border px-2 py-2" style={{ borderColor: C.otherAsset, background: C.bgAsset }}>
            <div className="text-sm font-semibold font-mono" style={{ color: C.text }}>{fmtUsd(slice.reinvest)}</div>
            <div className="mt-0.5 text-[9px]" style={{ color: C.muted }}>{t('wallet.perfDetail.reinvest', '复投')}</div>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <LegBlock
            t={t}
            title={t('wallet.perfDetail.leftZone', '左区业绩')}
            register={slice.left.register}
            reinvest={slice.left.reinvest}
            highlight={data.big_market_leg === 'L'}
          />
          <LegBlock
            t={t}
            title={t('wallet.perfDetail.rightZone', '右区业绩')}
            register={slice.right.register}
            reinvest={slice.right.reinvest}
            highlight={data.big_market_leg === 'R'}
          />
        </div>

        <p className="mt-3 text-[10px] leading-relaxed" style={{ color: C.muted }}>
          {t(
            'wallet.perfDetail.legHint',
            '左右区按该区直推下线当日新增/复投归属；一单只计入所在区，不会按比例拆到两边。',
          )}
        </p>
      </div>
    </div>
  );
}
